import type { CoReviewComment } from "@/shared/interfaces/CoReviewComment";

export interface SplitCoComments {
  narrativeComments: CoReviewComment[];
  nonNarrativeComments: CoReviewComment[];
  generalComments: CoReviewComment[];
  commentsBySection: Array<[string, CoReviewComment[]]>;
  narrativeCommentsByIndex: Map<number, CoReviewComment[]>;
}

// Buckets CO-review comments for the EditSectionPage rendering:
//  - narrativeComments: anchored to a narrative index (inline highlights)
//  - nonNarrativeComments: everything else (section / general)
//  - generalComments: sectionPath === "$" (report-wide feedback)
//  - commentsBySection: non-narrative, non-general, grouped by sectionPath
//  - narrativeCommentsByIndex: narrative comments grouped by their narrativeIndex
export function splitCoComments(
  comments: CoReviewComment[],
): SplitCoComments {
  const narrativeComments: CoReviewComment[] = [];
  const nonNarrativeComments: CoReviewComment[] = [];
  for (const c of comments) {
    if (typeof c.narrativeIndex === "number") {
      narrativeComments.push(c);
    } else {
      nonNarrativeComments.push(c);
    }
  }

  const generalComments = nonNarrativeComments.filter(
    (c) => c.sectionPath === "$",
  );

  const bySection = new Map<string, CoReviewComment[]>();
  for (const c of nonNarrativeComments) {
    if (c.sectionPath === "$") continue;
    const list = bySection.get(c.sectionPath) ?? [];
    list.push(c);
    bySection.set(c.sectionPath, list);
  }

  const narrativeCommentsByIndex = new Map<number, CoReviewComment[]>();
  for (const c of narrativeComments) {
    if (typeof c.narrativeIndex !== "number") continue;
    const list = narrativeCommentsByIndex.get(c.narrativeIndex) ?? [];
    list.push(c);
    narrativeCommentsByIndex.set(c.narrativeIndex, list);
  }

  return {
    narrativeComments,
    nonNarrativeComments,
    generalComments,
    commentsBySection: Array.from(bySection.entries()),
    narrativeCommentsByIndex,
  };
}

// Pulls narrative text per index from the report form data. Each Narratives
// entry may store the prose under `Text` or `Narrative`; missing/invalid
// entries get an empty string so indices line up with comment anchors.
export function extractNarrativeTexts(
  formData: Record<string, unknown> | null | undefined,
): Array<{ index: number; text: string }> {
  const out: Array<{ index: number; text: string }> = [];
  const narratives = formData?.["Narratives"];
  if (!Array.isArray(narratives)) return out;
  narratives.forEach((entry, index) => {
    if (!entry || typeof entry !== "object") {
      out.push({ index, text: "" });
      return;
    }
    const e = entry as Record<string, unknown>;
    const text =
      typeof e.Text === "string"
        ? e.Text
        : typeof e.Narrative === "string"
          ? e.Narrative
          : "";
    out.push({ index, text });
  });
  return out;
}
